import type {
  OnecDoc,
  OnecDocKind,
  OzonGroup,
  OzonRegistryRow,
} from "@/api/ozonSupply";
import { stageOf } from "./ozonStage";

/** Реестр поставок Ozon: чипы, «Внимание», фильтры, сортировка и группировка — чистыми
 *  функциями. Разметка страницы только рисует то, что посчитано здесь, и тест проверяет именно
 *  это: пока «что требует внимания» решалось в JSX, у каждой строки списка был свой ответ.
 *
 *  ⚠️ Группу статуса считает БЭКЕНД (`ozon_registry.group_of`) — здесь её не пересчитываем, а
 *  только раскладываем по чипам. Две копии правила разошлись бы на первом новом коде площадки.
 */

/* ────────────────────────── коды площадки ────────────────────────── */

/** `state` заявки из `/v3/supply-order/get` — по-русски. */
export const OZON_STATE_LABEL: Record<string, string> = {
  DATA_FILLING: "Заполнение данных",
  READY_TO_SUPPLY: "Готова к отгрузке",
  ACCEPTED_AT_SUPPLY_WAREHOUSE: "Принята на точке отгрузки",
  IN_TRANSIT: "В пути",
  ACCEPTANCE_AT_STORAGE_WAREHOUSE: "Приёмка на складе",
  REPORTS_CONFIRMATION_AWAITING: "Ждёт подтверждения актов",
  REPORT_REJECTED: "Акт отклонён",
  COMPLETED: "Завершена",
  REJECTED_AT_SUPPLY_WAREHOUSE: "Отклонена на точке отгрузки",
  CANCELLED: "Отменена",
  OVERDUE: "Просрочена",
};

/** Подпись кода площадки. `null` — статус ни разу не обновляли (экран пишет «не обновлялся»).
 *
 *  ⚠️ Незнакомый код — КАК ЕСТЬ, а не «неизвестно» и не ближайший похожий: площадка добавляет
 *  статусы без предупреждения, и по самому коду человек хотя бы найдёт его в документации.
 */
export function ozonStateLabel(code: string | null): string | null {
  if (!code) return null;
  return OZON_STATE_LABEL[code] ?? code;
}

/* ────────────────────────── чипы ────────────────────────── */

export type Chip =
  | "active"
  | "attention"
  | "prep"
  | "work"
  | "transit"
  | "acceptance"
  | "archive";

export const CHIPS: { key: Chip; label: string; title: string }[] = [
  {
    key: "active",
    label: "Активные",
    title: "Всё, что не в архиве: подготовка, в работе, в пути, приёмка и неизвестные статусы",
  },
  {
    key: "attention",
    label: "Внимание",
    title:
      "Устаревший ответ площадки, заявка без ответа, поломка 1С, документ 1С не найден / не проведён / помечен на удаление, заказ 1С закрыт раньше поставки, неизвестный статус, акт отклонён, окно приёмки прошло без отгрузки",
  },
  { key: "prep", label: "Подготовка", title: "Заявки ещё нет" },
  {
    key: "work",
    label: "В работе",
    title: "Заявка есть, статус площадки не обновляли или «Заполнение данных»",
  },
  {
    key: "transit",
    label: "В пути",
    title: "Готова к отгрузке, принята на точке отгрузки, в пути",
  },
  {
    key: "acceptance",
    label: "Приёмка",
    title: "Приёмка на складе, ждёт подтверждения актов, акт отклонён",
  },
  {
    key: "archive",
    label: "Архив",
    title: "Завершённые, отменённые и убранные в архив вручную",
  },
];

/** «Активные» — все незаархивированные, включая `unknown`: неизвестный статус не повод прятать
 *  поставку, наоборот. */
const ACTIVE: OzonGroup[] = ["prep", "work", "transit", "acceptance", "unknown"];

export function chipOf(asked: string): Chip {
  return CHIPS.some((c) => c.key === asked) ? (asked as Chip) : "active";
}

const SLOT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

const pad = (n: number): string => String(n).padStart(2, "0");

/** Местное «сейчас» в формате `YYYY-MM-DDTHH:MM:SS` — того же, что `slot_to`: строки без зоны
 *  сравниваем как строки, а не через `new Date` (см. правило в `slotFormat.ts`). */
function localIso(ms: number): string {
  const d = new Date(ms);
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/** Слот приёмки прошёл: `true`/`false`, `null` — окна нет, `"bad"` — дата не читается.
 *  Битая дата НЕ бросает исключение (роняла всю страницу): её показываем причиной «Внимания». */
function slotPassed(r: OzonRegistryRow, now: number): boolean | null | "bad" {
  if (!r.slot_to) return null;
  if (!SLOT_RE.test(r.slot_to)) {
    console.error(`slot_to поставки ${r.shipment_id} не читается: «${r.slot_to}»`);
    return "bad";
  }
  return r.slot_to < localIso(now);
}

/* ────────────────────────── документы 1С ────────────────────────── */

/** Порядок цепочки: заказ покупателя → ПТМО → счёт-фактура. */
export const ONEC_DOC_KINDS: OnecDocKind[] = ["order", "transfer", "invoice"];

/** Подписи документа: `short` — в ячейке и в отчёте, `full` — в подсказке и причинах. */
export const ONEC_DOC_LABEL: Record<OnecDocKind, { short: string; full: string }> = {
  order: { short: "заказ", full: "заказ покупателя" },
  transfer: { short: "ПТМО", full: "ПТМО" },
  invoice: { short: "СФ", full: "счёт-фактура" },
};

/** Род слова у документа — «не проведён» / «не проведена». ПТМО — передача (ж. р.). */
const FEM: Record<OnecDocKind, boolean> = {
  order: false,
  transfer: true,
  invoice: false,
};

const word = (kind: OnecDocKind, m: string, f: string): string =>
  FEM[kind] ? f : m;

/** Состояние документа одним словом-тоном: что рисовать и что считать проблемой. Порядок —
 *  от худшего: не найден → помечен на удаление → не проведён → проведён. `unknown` — 1С не
 *  сказала, проведён ли (posted=null при found=true): показываем вслух, не угадываем. */
export type OnecDocTone = "missing" | "deleted" | "unposted" | "posted" | "unknown";

export function onecDocTone(d: OnecDoc): OnecDocTone {
  if (!d.found) return "missing";
  if (d.deleted === true) return "deleted";
  if (d.posted === false) return "unposted";
  if (d.posted === true) return "posted";
  return "unknown";
}

/** Короткое слово к тону — для метки в ячейке. */
export function onecToneWord(kind: OnecDocKind, tone: OnecDocTone): string {
  switch (tone) {
    case "missing":
      return "нет в 1С";
    case "deleted":
      return "на удалении";
    case "unposted":
      return word(kind, "не проведён", "не проведена");
    case "posted":
      return word(kind, "проведён", "проведена");
    case "unknown":
      return "проведение неизвестно";
  }
}

/** Строка подсказки про один документ: номер, состояние, проведён ли, пометка удаления. */
export function onecDocLine(kind: OnecDocKind, d: OnecDoc | null): string {
  const name = ONEC_DOC_LABEL[kind].full;
  const title = name.charAt(0).toLocaleUpperCase("ru") + name.slice(1);
  if (d === null) return `${title}: на поставке нет`;
  if (!d.found) return `${title} ${d.number}: ${word(kind, "не найден", "не найдена")} в 1С`;
  const parts: string[] = [];
  if (kind === "order")
    parts.push(d.state ? `состояние «${d.state}»` : "состояние в 1С не указано");
  parts.push(
    d.posted === null
      ? "проведён ли — 1С не сказала"
      : d.posted
        ? word(kind, "проведён", "проведена")
        : word(kind, "не проведён", "не проведена"),
  );
  parts.push(
    d.deleted === null
      ? "пометка удаления — 1С не сказала"
      : d.deleted
        ? word(kind, "помечен на удаление", "помечена на удаление")
        : "без пометки удаления",
  );
  if (kind === "order" && d.closed === true) parts.push("закрыт");
  return `${title} ${d.number}: ${parts.join(", ")}`;
}

/** Документ цепочки 1С глазами реестра: что записано у нас сейчас (`number`) и, если он есть, —
 *  снимок состояния из 1С. `doc=null` при `number!=null` — снимок документа не брали или он
 *  устарел (см. `onecDocsView`). `number=null` — документа на поставке нет. */
export interface OnecDocView {
  kind: OnecDocKind;
  number: string | null;
  doc: OnecDoc | null;
}

/** Сверка снимка `onec_docs` с текущей цепочкой `r.onec`. Снимок пишется только кнопкой, а портал
 *  между нажатиями создаёт и убирает ПТМО и СФ, поэтому снимок документа годен, только если его
 *  номер совпал с текущим номером в `r.onec`:
 *  - номер в `r.onec` есть, а в снимке документа нет или номер другой → `doc=null` (покажем
 *    по `r.onec` «состояние в 1С не запрашивалось»);
 *  - в снимке документ есть, а в `r.onec` его уже нет → `number=null`, снимок отброшен.
 *  Нет `r.onec` (документов нет) — null: снимок не показываем вообще. Ячейка и причины
 *  «Внимания» берут документы только отсюда. */
export function onecDocsView(r: OzonRegistryRow): OnecDocView[] | null {
  const onec = r.onec;
  if (!onec) return null;
  const current: Record<OnecDocKind, string | null> = {
    order: onec.order_number,
    transfer: onec.transfer_number,
    invoice: onec.invoice_number,
  };
  return ONEC_DOC_KINDS.map((kind) => {
    const number = current[kind];
    const snap = r.onec_docs?.[kind] ?? null;
    return {
      kind,
      number,
      doc: number !== null && snap !== null && snap.number === number ? snap : null,
    };
  });
}

/** Группы, в которых 1С ещё спрашивают и где её снимок живой. В архиве (done/cancelled) снимок
 *  больше не обновляется — причин по нему не берём. */
const ONEC_LIVE: OzonGroup[] = ["prep", "work", "transit", "acceptance", "unknown"];

/** Группы, где закрытый в 1С заказ при незавершённой поставке — тревога. ⚠️ Только prep и work:
 *  замер прода — в УНФ заказ ставят «Завершен» сразу после отгрузки, поэтому при transit и
 *  acceptance это норма (33 ложных тревоги). */
const ORDER_CLOSED_EARLY: OzonGroup[] = ["prep", "work"];

/** Проблемы документов 1С по снимку — причины «Внимания». Только по документам, чей снимок
 *  сошёлся с текущим номером (`onecDocsView`); нет снимка — нет и причин: состояние в 1С не
 *  спрашивали, и ячейка «1С» говорит об этом сама. В архиве причин нет.
 *
 *  ⚠️ Непроведённые ПТМО/СФ — тоже причина: цикл 1С не закрыт, товар может числиться не там.
 *  `closed=null` — 1С признак не отдала, правило «заказ закрыт раньше поставки» молчит. */
export function onecReasons(r: OzonRegistryRow): string[] {
  if (!ONEC_LIVE.includes(r.group)) return [];
  const view = onecDocsView(r);
  if (!view) return [];
  const out: string[] = [];
  for (const { kind, doc: d } of view) {
    if (!d) continue;
    const name = `${ONEC_DOC_LABEL[kind].full} ${d.number}`;
    const tone = onecDocTone(d);
    if (tone === "missing")
      out.push(`${name} ${word(kind, "не найден", "не найдена")} в 1С`);
    else if (tone === "deleted")
      out.push(`${name} ${word(kind, "помечен", "помечена")} на удаление в 1С`);
    else if (tone === "unposted")
      out.push(`${name} ${word(kind, "не проведён", "не проведена")} в 1С`);
  }
  const o = view.find((v) => v.kind === "order")?.doc;
  if (o && o.found && o.closed === true && ORDER_CLOSED_EARLY.includes(r.group))
    out.push(
      `заказ покупателя ${o.number} закрыт в 1С${o.state ? ` («${o.state}»)` : ""}, а поставка ещё не завершена`,
    );
  return out;
}

/** Почему строка требует внимания — словами, для подсказки. Пусто — всё в порядке. */
export function attentionReasons(
  r: OzonRegistryRow,
  now: number = Date.now(),
): string[] {
  const out: string[] = [];
  const stage = stageOf(r, now);
  if (stage === "draft_stale") out.push("ответ площадки устарел");
  if (stage === "ordering") out.push("заявка отправлена, ответа не было");
  if (r.broken) out.push("цикл 1С сломан");
  if (r.group === "unknown")
    out.push(`неизвестный статус Ozon: ${r.ozon_order_state ?? "—"}`);
  if (r.ozon_order_state === "REPORT_REJECTED") out.push("акт отклонён");
  if (r.group === "prep" || r.group === "work") {
    const passed = slotPassed(r, now);
    if (passed === "bad") out.push(`дата окна не читается: «${r.slot_to}»`);
    else if (passed) {
      if (r.group === "work" && r.ozon_order_state == null)
        out.push(
          "окно приёмки прошло, статус площадки не обновлялся — нажмите «Обновить статусы»",
        );
      else out.push("окно приёмки прошло, а поставка не уехала");
    }
  }
  out.push(...onecReasons(r));
  return out;
}

export function needsAttention(
  r: OzonRegistryRow,
  now: number = Date.now(),
): boolean {
  return attentionReasons(r, now).length > 0;
}

/** Попадает ли живая (не архивная) строка под чип. Архив — отдельный запрос, сюда не ходит. */
export function inChip(
  r: OzonRegistryRow,
  chip: Exclude<Chip, "archive">,
  now: number = Date.now(),
): boolean {
  switch (chip) {
    case "active":
      return ACTIVE.includes(r.group);
    case "attention":
      return needsAttention(r, now);
    default:
      return r.group === chip;
  }
}

/** Числа на чипах — по ЖИВЫМ строкам. У «Архива» число известно, только когда он загружен:
 *  грузить его ради одной цифры — значит тянуть все завершённые поставки на каждом открытии. */
export function chipCounts(
  live: OzonRegistryRow[],
  archived: OzonRegistryRow[] | undefined,
  now: number = Date.now(),
): Record<Chip, number | null> {
  const count = (c: Exclude<Chip, "archive">): number =>
    live.filter((r) => inChip(r, c, now)).length;
  return {
    active: count("active"),
    attention: count("attention"),
    prep: count("prep"),
    work: count("work"),
    transit: count("transit"),
    acceptance: count("acceptance"),
    archive: archived ? archived.length : null,
  };
}

/* ────────────────────────── фильтры, сортировка, группы ────────────────────────── */

export interface RegistryFilter {
  /** юрлицо; "" — все */
  legal: string;
  /** номер фиксации; null — все */
  run: number | null;
  /** подстрока региона */
  q: string;
}

export function applyFilter(
  rows: OzonRegistryRow[],
  f: RegistryFilter,
): OzonRegistryRow[] {
  const q = f.q.trim().toLocaleLowerCase("ru");
  return rows.filter(
    (r) =>
      (!f.legal || r.legal_name === f.legal) &&
      (f.run === null || r.run_id === f.run) &&
      (!q || r.region.toLocaleLowerCase("ru").includes(q)),
  );
}

export type RegistrySort = "run" | "slot";

export const chooseSort = (asked: string): RegistrySort =>
  asked === "slot" ? "slot" : "run";

const byRunRegion = (a: OzonRegistryRow, b: OzonRegistryRow): number =>
  b.run_id - a.run_id || a.region.localeCompare(b.region, "ru");

/** `run` — фиксация от новых к старым, внутри — регион (как отдаёт бэкенд). `slot` — по дате
 *  приёмки от ближних к дальним; без окна — в конец. */
export function sortRows(
  rows: OzonRegistryRow[],
  sort: RegistrySort,
): OzonRegistryRow[] {
  const out = [...rows];
  if (sort === "run") return out.sort(byRunRegion);
  return out.sort((a, b) => {
    if (a.slot_from && b.slot_from)
      return a.slot_from.localeCompare(b.slot_from) || byRunRegion(a, b);
    if (a.slot_from) return -1;
    if (b.slot_from) return 1;
    return byRunRegion(a, b);
  });
}

export interface RunGroup {
  run_id: number;
  run_created_at: string;
  run_created_by: string;
  rows: OzonRegistryRow[];
}

/** Строки, разложенные по фиксациям: от новых к старым, порядок внутри — как пришёл. */
export function groupByRun(rows: OzonRegistryRow[]): RunGroup[] {
  const map = new Map<number, RunGroup>();
  for (const r of rows) {
    let g = map.get(r.run_id);
    if (!g) {
      g = {
        run_id: r.run_id,
        run_created_at: r.run_created_at,
        run_created_by: r.run_created_by,
        rows: [],
      };
      map.set(r.run_id, g);
    }
    g.rows.push(r);
  }
  return [...map.values()].sort((a, b) => b.run_id - a.run_id);
}

/** Когда статусы площадки обновляли последний раз — максимум по строкам; null — ни разу. */
export function lastRefreshedAt(rows: OzonRegistryRow[]): string | null {
  let best: string | null = null;
  let bestT = -Infinity;
  for (const r of rows) {
    if (!r.ozon_order_state_at) continue;
    const t = new Date(r.ozon_order_state_at).getTime();
    if (t > bestT) {
      bestT = t;
      best = r.ozon_order_state_at;
    }
  }
  return best;
}
